import { BodyLarge, Button, colorVariants, Heading2 } from '@casavo/base-ui'
import cn from 'classnames'
import { Formik, Form } from 'formik'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import React, { useEffect, useRef, useState } from 'react'
import { useSessionStorage } from 'usehooks-ts'
import * as yup from 'yup'

import Icon from '@/components/Icon'
import { useAuthentication } from '@/context/AuthProvider'
import { useBottomSheet } from '@/context/BottomSheetContext'
import { useDevice } from '@/context/Device'
import { Toast } from '@/design-lib/components/Toast'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { initApi, BookVisitPayload, RequestInfoPayload } from '@/lib/api-gluglu-auth'
import { ListingCate } from '@/page-commons/listingBySlug'
import { backgroundColor, gridCenter, marginBottom } from '@/shared-styles/styles.css'
import { HourRangeOption } from '@/types/visit'
import { getContactDates, getReminderLocalizedDate, mapDatesToLabelValue } from '@/utils/date-utils'
import { encodeVisitHours } from '@/utils/encode-visit-hours'
import { getGAClientId } from '@/utils/get-ga-client-id'
import { isStorageSupported } from '@/utils/isStorageSupported'
import { getPhoneNumberValidator } from '@/utils/phoneNumberValidator'
import { getPrefixFromCountryCode, langToCountryCode } from '@/utils/prefix'
import { handleClick } from '@/utils/tracking/eventsHandlers'

import { UserAuthentication } from '../auth/UserAuthentication'
import { ConceptImage } from '../ConceptImage'
import CasavoPropertyTitle from '../ContactAgencyForm/CasavoPropertyTitle'
import Chevron from '../Icon/Chevron'
import { Translated } from '../Translated'

import { RequestType } from '.'

import { RequestContactForm } from './RequestContactForm'
import { RequestContactSlot } from './RequestContactSlot'
import { RequestInfo } from './RequestInfo'
import { errorToast, maxWidthZero } from './styles.css'

type Props = {
  listing: ListingCate
  onSuccess: (requestType: RequestType) => void
  requestType: RequestType
}

export enum InfoSelectionEnum {
  MORE_PHOTOS = 'MORE_PHOTOS',
  OTHER = 'OTHER',
  VISIT = 'VISIT',
}

enum ERROR_ENUM {
  ALREADY_BOOKED_SLOT = 'ALREADY_BOOKED_SLOT',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

enum WIZARD_ENUM {
  FIRST_STEP = 'FIRST_STEP',
  FORM_STEP = 'FORM_STEP',
  LOGIN_STEP = 'LOGIN_STEP',
}

export const getValidationSchema = (isVisitRequestType: boolean, t: (key: string, query?: any) => string) => {
  const standardParams = {
    consentForCasavoMortgageService: yup.boolean(),
    date: yup.string().required(t('common:required-field-message')),
    hasAcceptedMarketing: yup.boolean(),
    hourRange: yup.string().required(t('common:required-field-message')),
    name: yup.string().required(t('common:required-field-message')),
    phoneNumber: getPhoneNumberValidator(t('common:required-field-message'), t('common:invalid-phone-message')),
    phoneNumberPrefix: yup.string().required(),
  }
  const visitParams = {
    date: isVisitRequestType ? yup.string().required(t('common:required-field-message')) : yup.string(),
    hourRange: isVisitRequestType ? yup.string().required(t('common:required-field-message')) : yup.string(),
  }
  const infoParams = {
    reason: !isVisitRequestType
      ? yup
          .mixed<Exclude<InfoSelectionEnum, InfoSelectionEnum.VISIT> | null>()
          .oneOf([InfoSelectionEnum.MORE_PHOTOS, InfoSelectionEnum.OTHER])
          .required(t('common:required-field-message'))
      : yup
          .mixed<Exclude<InfoSelectionEnum, InfoSelectionEnum.VISIT> | null>()
          .oneOf([InfoSelectionEnum.MORE_PHOTOS, InfoSelectionEnum.OTHER, null]),
  }

  return yup.object({ ...standardParams, ...visitParams, ...infoParams })
}

type FormFields = yup.InferType<ReturnType<typeof getValidationSchema>>

export const REQUEST_VISIT_INFO_AFTER_LOGIN = 'CASAVO@REQUEST_VISIT_INFO_AFTER_LOGIN'

interface InfoStoredValues {
  reason: Exclude<InfoSelectionEnum, InfoSelectionEnum.VISIT>
  requestType: 'info'
}

interface VisitStoredValues {
  date: string
  hourRange: string
  requestType: 'visit'
}

export type RequestContactStoredValues = InfoStoredValues | VisitStoredValues

const getDatetime = (initDate: string, hourRange: string) => {
  const date = new Date(initDate)
  const hrs = hourRange !== '' ? parseInt(hourRange.split(' - ')[0], 10) : date.getHours()
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hrs, 0, 0, 0)
}

export const RequestContactFlow: React.FC<Props> = ({ listing, onSuccess, requestType }) => {
  const [step, setStep] = useState<WIZARD_ENUM>(WIZARD_ENUM.FIRST_STEP)
  const [submitting, setSubmitting] = useState(false)
  const [showErrorMessage, setShowErrorMessage] = useState(false)
  const [currentRequestType, setCurrentRequestType] = useState<RequestType>(requestType)
  // const [isEmailInvalid, setIsEmailInvalid] = useState(false)
  const [hasError, setError] = useState<ERROR_ENUM | undefined>(undefined)
  const { closeBottomSheet, openBottomSheet } = useBottomSheet()
  const device = useDevice()
  const { isLoading, user } = useAuthentication()
  const [storedRequestData, setStoredRequestData] = useSessionStorage<null | RequestContactStoredValues>(
    REQUEST_VISIT_INFO_AFTER_LOGIN,
    null
  )

  const isVisitRequestType = currentRequestType === 'visit'

  const errorRef = useRef<HTMLDivElement>(null)

  const { lang, t } = useTranslation()
  const validationSchema = getValidationSchema(isVisitRequestType, t)

  const defaultLeadPhoneNumberPrefix = getPrefixFromCountryCode(langToCountryCode(lang))

  const dateList = isVisitRequestType ? getContactDates(!isVisitRequestType, 15) : []
  const initialDate = storedRequestData?.requestType === 'visit' ? storedRequestData?.date || '' : ''
  const initHourRange = storedRequestData?.requestType === 'visit' ? storedRequestData?.hourRange || '' : ''
  const initReason = storedRequestData?.requestType === 'info' ? storedRequestData?.reason || null : null

  const { bookVisit, requestInfoCasavoListing } = initApi()

  useEffect(() => {
    if (showErrorMessage) {
      errorRef?.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [showErrorMessage])

  useEffect(() => {
    if (user && !isLoading && storedRequestData !== null) {
      setStep(WIZARD_ENUM.FORM_STEP)
      if (device === 'mobile') {
        closeBottomSheet()
      }
    }
    return () => {
      if (user && !isLoading && storedRequestData !== null) {
        isStorageSupported(() => sessionStorage) && sessionStorage.removeItem(REQUEST_VISIT_INFO_AFTER_LOGIN)
      }
    }
  }, [user, isLoading, storedRequestData])

  async function onSubmitInfo(values: FormFields) {
    setShowErrorMessage(false)

    try {
      setSubmitting(true)

      const payload: RequestInfoPayload = {
        consent_for_service_mortgage: values.consentForCasavoMortgageService,
        consent_for_updates: values.hasAcceptedMarketing,
        current_language: lang.toUpperCase() as 'IT' | 'ES' | 'EN' | 'PT',
        name: values.name,
        phone_number: values.phoneNumberPrefix + values.phoneNumber.replace(/-/g, ''),
        reason: values.reason,
        tracking_client_id: getGAClientId(),
      }

      await requestInfoCasavoListing(listing.id, payload)

      setSubmitting(false)
      onSuccess(currentRequestType)
    } catch (e) {
      setShowErrorMessage(true)
      setSubmitting(false)
    }
  }

  async function onSubmitVisit(values: FormFields) {
    setShowErrorMessage(false)
    // setIsEmailInvalid(false)

    try {
      setSubmitting(true)
      const visitSlot = encodeVisitHours(new Date(values.date), values.hourRange as HourRangeOption)

      const payload: BookVisitPayload = {
        consentForCasavoMortgageService: values.consentForCasavoMortgageService,
        currentLanguage: lang,
        keepMeUpdated: values.hasAcceptedMarketing,
        name: values.name,
        phoneNumber: values.phoneNumberPrefix + values.phoneNumber.replace(/-/g, ''),
        trackingClientId: getGAClientId(),
        visitSlot,
      }

      await bookVisit(listing.id, payload)

      setSubmitting(false)
      onSuccess(currentRequestType)
    } catch (e) {
      setShowErrorMessage(true)
      setSubmitting(false)
    }
  }

  const onBack = () => {
    setStep(WIZARD_ENUM.FIRST_STEP)
  }

  const onInfoSelection = (reason: InfoSelectionEnum) => {
    if (reason === InfoSelectionEnum.VISIT) {
      setCurrentRequestType('visit')
      return
    }
    if (user) {
      setStep(WIZARD_ENUM.FORM_STEP)
    } else {
      setStoredRequestData({ reason, requestType: 'info' })
      showLoginWall()
    }
  }

  const showLoginWall = () => {
    if (device === 'mobile') {
      openBottomSheet(
        <UserAuthentication
          conceptImage={<ConceptImage height={80} name={isVisitRequestType ? 'write' : 'phone'} width={80} />}
          mainTitle={isVisitRequestType ? 'Login_RequestVisit_BottomSheet_Title' : 'Login_FromContactFrom_Title'}
          returnUrl={window.location.href}
          touchPoint={isVisitRequestType ? 'FromBookVisit' : 'FromContactForm'}
          onLoggedIn={() => closeBottomSheet()}
        />,
        { minHeight: 610 }
      )
    } else {
      setStep(WIZARD_ENUM.LOGIN_STEP)
    }
  }

  const onContinue = ({ date, hourRange }: FormFields) => {
    handleClick(isVisitRequestType ? 'BookVisitModalForm' : 'ScheduleCallModalForm', 'Continue')
    if (user) {
      setStep(WIZARD_ENUM.FORM_STEP)
    } else {
      setStoredRequestData({ date, hourRange, requestType: 'visit' })
      showLoginWall()
    }
  }

  if (step === WIZARD_ENUM.LOGIN_STEP) {
    return (
      <div className={backgroundColor.lightBlue}>
        <UserAuthentication
          conceptImage={<ConceptImage height={120} name={isVisitRequestType ? 'write' : 'phone'} width={120} />}
          mainTitle={isVisitRequestType ? 'Login_RequestVisit_BottomSheet_Title' : 'Login_FromContactFrom_Title'}
          returnUrl={window.location.href}
          touchPoint={isVisitRequestType ? 'FromBookVisit' : 'FromContactForm'}
        />
      </div>
    )
  }

  return (
    <div
      className={cn(
        sprinkles({ display: 'flex', flexDirection: 'column', height: '100%', padding: 'xl' }),
        backgroundColor.offWhite
      )}
    >
      {isVisitRequestType && (
        <>
          <Heading2 className={marginBottom.l}>
            <Translated
              components={{
                0: <strong className={colorVariants.deepGreen600} />,
              }}
              i18Key={'listing-detail:request_visit_modal_title'}
            />
          </Heading2>

          <CasavoPropertyTitle className={marginBottom.xl} titles={listing.detail.titles} />
        </>
      )}
      {!isVisitRequestType && (
        <>
          <Heading2 className={marginBottom.l}>{t('listing-detail:request_call')}</Heading2>
          {step === WIZARD_ENUM.FIRST_STEP && (
            <BodyLarge strong className={sprinkles({ marginBottom: '2xl' })}>
              {t('contact-form:casavo.reason.placeholder')}
            </BodyLarge>
          )}
        </>
      )}

      <Formik
        initialValues={{
          consentForCasavoMortgageService: false,
          date: initialDate,
          hasAcceptedMarketing: false,
          hourRange: initHourRange,
          name: '',

          phoneNumber: '',
          phoneNumberPrefix: defaultLeadPhoneNumberPrefix,
          reason: initReason,
        }}
        validationSchema={validationSchema}
        onSubmit={isVisitRequestType ? onSubmitVisit : onSubmitInfo}
      >
        {({ values }) => (
          <>
            <Form
              className={sprinkles({
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
              })}
            >
              {step === WIZARD_ENUM.FIRST_STEP && isVisitRequestType && (
                <>
                  <RequestContactSlot
                    dateList={mapDatesToLabelValue(dateList, lang)}
                    isVisitRequestType={isVisitRequestType}
                  />
                  <div
                    className={cn(
                      requestType === 'info' && gridCenter,
                      sprinkles({ marginBottom: { mobile: 'm', tablet: 'xl' }, marginTop: 'auto' })
                    )}
                  >
                    {requestType === 'info' && (
                      <Button
                        ariaLabel="back to previous step"
                        className={maxWidthZero}
                        Icon={() => <Chevron direction="left" />}
                        styleVariant="tertiary"
                        onClick={() => setCurrentRequestType('info')}
                      />
                    )}
                    <Button
                      iconRight
                      className={sprinkles({
                        alignSelf: 'center',
                        marginX: 'auto',
                      })}
                      data-testid="next-button"
                      disabled={values.date === '' || values.hourRange === ''}
                      Icon={() => <Icon.Chevron />}
                      label={t('listing-detail:schedule_visit_flow_next_button')}
                      minWidth={210}
                      styleVariant="primary"
                      type="button"
                      onClick={() => {
                        onContinue(values)
                      }}
                    />
                  </div>
                </>
              )}
              {step === WIZARD_ENUM.FIRST_STEP && currentRequestType === 'info' && (
                <RequestInfo onSelection={onInfoSelection} />
              )}
              {step === WIZARD_ENUM.FORM_STEP && (
                <>
                  {isVisitRequestType && (
                    <BodyLarge strong className={marginBottom.xl}>
                      <Trans
                        i18nKey={
                          isVisitRequestType
                            ? 'listing-detail:RequestVisit_Modal_Recap'
                            : 'listing-detail:BookCall_Modal_Recap'
                        }
                        values={getReminderLocalizedDate(getDatetime(values.date, values.hourRange), lang)}
                      />
                    </BodyLarge>
                  )}
                  <RequestContactForm
                    isVisitRequestType={isVisitRequestType}
                    showErrorMessage={showErrorMessage}
                    submitting={submitting}
                    onBack={onBack}
                  />
                  {hasError && (
                    <div data-testid="request-visit-error">
                      <Toast className={errorToast} styleType="error" title="">
                        {hasError === ERROR_ENUM.ALREADY_BOOKED_SLOT && (
                          <Trans components={[<br key="0" />]} i18nKey="listing-detail:slot_already_booked_error" />
                        )}
                        {hasError === ERROR_ENUM.GENERIC_ERROR && (
                          <Trans components={[<br key="0" />]} i18nKey="listing-detail:request_visit_error" />
                        )}
                      </Toast>
                    </div>
                  )}
                </>
              )}
            </Form>
          </>
        )}
      </Formik>
    </div>
  )
}
